import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import {
  feedErrorMessage,
  loadModerationQueue,
  moderateFeedItem,
  ModerationItem,
  ReportReason,
} from "../firebase/feed";

const REASON_LABEL: Record<ReportReason, string> = {
  offensive: "Offensive",
  spam: "Spam",
  misleading: "Misleading",
  other: "Other",
};

const itemKey = (i: ModerationItem) => `${i.postId}/${i.commentId ?? ""}`;

/** Reported posts and comments, for allowlisted moderators. */
export default function FeedModeration() {
  const [items, setItems] = useState<ModerationItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [working, setWorking] = useState<string | null>(null);
  const [actionError, setActionError] = useState<{ key: string; message: string } | null>(null);

  const load = useCallback(() => {
    setLoadError(null);
    loadModerationQueue()
      .then(setItems)
      .catch((err) => setLoadError(feedErrorMessage(err)));
  }, []);
  useEffect(load, [load]);

  const act = async (item: ModerationItem, action: "restore" | "remove") => {
    const key = itemKey(item);
    setWorking(key);
    setActionError(null);
    try {
      await moderateFeedItem({ postId: item.postId, commentId: item.commentId }, action);
      setItems((list) => list?.filter((i) => itemKey(i) !== key) ?? list);
    } catch (err) {
      setActionError({ key, message: feedErrorMessage(err) });
    } finally {
      setWorking(null);
    }
  };

  const buttonClass =
    "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150";

  return (
    <>
      <Link
        to="/feed"
        className="inline-flex items-center gap-1.5 -ml-2 px-2 py-1.5 rounded-md text-sm text-muted hover:bg-subtle hover:text-content focus:outline-none focus-visible:ring-2 focus-visible:ring-focus transition-colors duration-150"
      >
        <ArrowLeft size={15} aria-hidden="true" />
        All debates
      </Link>
      <div className="mt-3 pb-3 border-b border-line flex items-baseline justify-between gap-3">
        <h1 className="text-lg font-semibold tracking-tight text-content">Reported posts and comments</h1>
        {items && <span className="text-sm text-muted"><span className="font-mono tabular-nums">{items.length}</span> in queue</span>}
      </div>

      {loadError && (
        <div className="py-8">
          <p role="alert" className="text-sm text-danger">{loadError}</p>
          <button type="button" onClick={load} className="mt-2 text-sm font-medium text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-focus rounded-sm">
            Try again
          </button>
        </div>
      )}
      {!items && !loadError && <p className="py-8 text-sm text-muted" role="status">Loading reports…</p>}
      {items && items.length === 0 && <p className="py-8 text-sm text-muted">Nothing has been reported.</p>}

      {items?.map((item) => {
        const key = itemKey(item);
        const busy = working === key;
        return (
          <article key={key} className="py-4 border-b border-line">
            <div className="flex items-center gap-2 flex-wrap text-xs text-muted">
              <span className="font-medium text-content">{item.kind === "post" ? "Debate" : "Comment"}</span>
              <span aria-hidden="true">·</span>
              <span>{item.authorName}</span>
              <span aria-hidden="true">·</span>
              <span>
                <span className="font-mono tabular-nums">{item.reportCount}</span> {item.reportCount === 1 ? "report" : "reports"}
                {Object.keys(item.reasons).length > 0 && (
                  <> ({Object.entries(item.reasons).map(([r, n]) => `${REASON_LABEL[r as ReportReason] ?? r} ${n}`).join(", ")})</>
                )}
              </span>
              {item.hidden && <span className="px-1.5 py-0.5 rounded-md border border-danger-line text-danger">Hidden from feed</span>}
              {item.contentWarning && <span className="px-1.5 py-0.5 rounded-md border border-line">Content warning: {item.contentWarning}</span>}
            </div>
            {item.kind === "comment" && (
              <p className="mt-1.5 text-xs text-muted truncate">On “{item.question}”</p>
            )}
            <p className="mt-1.5 text-sm font-semibold text-content break-words">
              {item.kind === "post" ? item.question : item.text}
            </p>
            {item.kind === "post" && item.text && <p className="mt-1 text-sm text-muted line-clamp-2">{item.text}</p>}
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <button
                type="button"
                disabled={busy}
                onClick={() => act(item, "restore")}
                className={`${buttonClass} border border-line bg-surface text-content hover:bg-subtle`}
              >
                {item.hidden ? "Restore to feed" : "Dismiss reports"}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => act(item, "remove")}
                className={`${buttonClass} bg-danger-hover text-on-solid hover:opacity-90`}
              >
                {busy && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
                Remove permanently
              </button>
              <Link to={`/feed/${item.postId}`} className={`${buttonClass} text-muted hover:bg-subtle hover:text-content`}>
                Open
              </Link>
              {actionError?.key === key && <span role="alert" className="text-xs text-danger">{actionError.message}</span>}
            </div>
          </article>
        );
      })}
    </>
  );
}
